import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, CloseButton, Offcanvas } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAddressCard,
  faClock,
  faExternalLinkAlt,
  faFingerprint,
  faInfoCircle,
  faMapMarkedAlt,
  faRedoAlt,
  faSitemap,
  faUserCheck,
  faUserTimes
} from '@fortawesome/free-solid-svg-icons';
import useRequestState from 'hooks/useRequestState';
import { fetchRegistration, getApiErrorMessage } from 'helpers/registrationApi';
import paths from 'routes/paths';
import {
  FacilityChips,
  InitialsAvatar,
  RolePill,
  StatusPill,
  TonePill,
  ToneIcon
} from 'components/registration-shared/RegistrationUI';
import {
  formatDateTime,
  formatPhone,
  formatRelative,
  getApprovedUserId,
  getFullName,
  getIpAddress,
  getRejectionReason,
  getRequestedFacilities,
  getRequestedRoleId,
  getReviewedAt,
  getReviewerName,
  getStatus,
  getSubmittedAt,
  getTypedOrganization,
  getUserAgent,
  hasGroup
} from 'components/registration-shared/registrationFormat';

const Section = ({ icon, tone, title, children }) => (
  <section className="mym-drawer-section">
    <div className="mym-drawer-section-title">
      <ToneIcon icon={icon} tone={tone} size="sm" />
      {title}
    </div>
    {children}
  </section>
);

Section.propTypes = {
  icon: PropTypes.object.isRequired,
  tone: PropTypes.string.isRequired,
  title: PropTypes.node.isRequired,
  children: PropTypes.node
};

const Muted = ({ children }) => (
  <span className="text-500 fw-normal">{children}</span>
);

Muted.propTypes = { children: PropTypes.node };

/**
 * Read-only record of a request that has already been approved or rejected
 * (U4.1) — the access requested, the decision, contact, address and the
 * submission trail (submitted at, IP). Pending requests open in the review
 * modal instead, so there are no actions here. Opens instantly with the list
 * row and fills in the rest from GET /users/registrations/:id.
 */
const RegistrationDetailDrawer = ({
  show,
  record,
  onHide,
  getGroupName,
  lookupFacility
}) => {
  const detailReq = useRequestState();
  const { run: runDetail } = detailReq;
  const recordId = record?.id;

  useEffect(() => {
    if (!show || recordId === undefined || recordId === null) {
      return;
    }
    runDetail(() => fetchRegistration(recordId), { keepData: false });
  }, [show, recordId, runDetail]);

  if (!record) {
    return null;
  }

  const detail =
    detailReq.isSuccess &&
    String(detailReq.data?.id ?? recordId) === String(recordId)
      ? detailReq.data
      : {};
  const merged = { ...record, ...detail };
  const status = getStatus(merged);
  const submittedAt = getSubmittedAt(merged);
  const reviewedAt = getReviewedAt(merged);
  const reviewer = getReviewerName(merged);
  const requested = getRequestedFacilities(merged, lookupFacility);
  const approvedUserId = getApprovedUserId(merged);
  const addressLines = [
    merged.address,
    merged.address2,
    [merged.city, [merged.state, merged.zip].filter(Boolean).join(' ')]
      .filter(Boolean)
      .join(', ')
  ].filter(line => String(line || '').trim());
  const userAgent = getUserAgent(merged);

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      className="mym-queue-drawer"
      aria-labelledby="registration-drawer-title"
    >
      <div className="mym-drawer-hero">
        <CloseButton onClick={onHide} aria-label="Close details" />
        <div className="d-flex align-items-center gap-3 pe-4">
          <InitialsAvatar record={merged} size="lg" />
          <div className="min-w-0">
            <h2
              id="registration-drawer-title"
              className="mym-drawer-name text-truncate"
            >
              {getFullName(merged)}
            </h2>
            <div className="mym-drawer-email">{merged.email}</div>
            <div className="d-flex flex-wrap gap-2 mt-2">
              <StatusPill status={status} />
              {submittedAt && (
                <TonePill tone="secondary" icon={faClock}>
                  {formatRelative(submittedAt)}
                </TonePill>
              )}
            </div>
          </div>
        </div>
      </div>

      <Offcanvas.Body>
        {detailReq.isLoading && (
          <div className="px-4 pt-3" aria-live="polite">
            <div className="mym-skeleton-line mb-2" style={{ width: '60%' }} />
            <div className="mym-skeleton-line" style={{ width: '40%' }} />
          </div>
        )}
        {detailReq.isError && (
          <div className="px-4 pt-3">
            <div className="mym-inline-note mym-tone-warning">
              <FontAwesomeIcon icon={faInfoCircle} className="mt-1" />
              <div className="flex-1">
                Some details couldn't be loaded (
                {getApiErrorMessage(detailReq.error, 'unknown error')}).{' '}
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 align-baseline fw-semibold"
                  onClick={() =>
                    runDetail(() => fetchRegistration(recordId), {
                      keepData: false
                    })
                  }
                >
                  <FontAwesomeIcon icon={faRedoAlt} className="me-1" />
                  Retry
                </Button>
              </div>
            </div>
          </div>
        )}

        <Section icon={faSitemap} tone="success" title="Access requested">
          <dl className="mym-kv">
            <dt>Organization</dt>
            <dd>
              {!hasGroup(merged) &&
              !merged.group_name &&
              getTypedOrganization(merged) ? (
                <>
                  {getTypedOrganization(merged)}
                  <div className="fs-11 fw-normal text-600">
                    Entered by the applicant — not matched to a group
                  </div>
                </>
              ) : (
                getGroupName(merged)
              )}
            </dd>
            <dt>Role</dt>
            <dd>
              {getRequestedRoleId(merged) ? (
                <RolePill roleId={getRequestedRoleId(merged)} />
              ) : (
                <Muted>Not specified</Muted>
              )}
            </dd>
            <dt>Facilities</dt>
            <dd>
              <FacilityChips
                facilities={requested}
                max={0}
                emptyText="Not specified"
              />
            </dd>
          </dl>
        </Section>

        {status === 'approved' && (
          <Section icon={faUserCheck} tone="success" title="Approval">
            <dl className="mym-kv">
              <dt>Approved by</dt>
              <dd>{reviewer || <Muted>Unknown</Muted>}</dd>
              <dt>Approved on</dt>
              <dd>{formatDateTime(reviewedAt) || <Muted>Unknown</Muted>}</dd>
              {approvedUserId && (
                <>
                  <dt>Account</dt>
                  <dd>
                    <Link
                      to={`${paths.viewfacilitystaffprofile}/${approvedUserId}`}
                      state={{ userid: approvedUserId }}
                    >
                      Open user profile
                      <FontAwesomeIcon
                        icon={faExternalLinkAlt}
                        className="ms-1 fs-11"
                      />
                    </Link>
                  </dd>
                </>
              )}
            </dl>
          </Section>
        )}

        {status === 'rejected' && (
          <Section icon={faUserTimes} tone="danger" title="Rejection">
            <div className="mym-reason-quote mb-3">
              {getRejectionReason(merged) || 'No reason recorded.'}
            </div>
            <dl className="mym-kv">
              <dt>Rejected by</dt>
              <dd>{reviewer || <Muted>Unknown</Muted>}</dd>
              <dt>Rejected on</dt>
              <dd>{formatDateTime(reviewedAt) || <Muted>Unknown</Muted>}</dd>
            </dl>
          </Section>
        )}

        <Section icon={faAddressCard} tone="primary" title="Contact">
          <dl className="mym-kv">
            <dt>Email</dt>
            <dd>
              {merged.email ? (
                <a href={`mailto:${merged.email}`}>{merged.email}</a>
              ) : (
                <Muted>Not provided</Muted>
              )}
            </dd>
            <dt>Phone</dt>
            <dd>
              {merged.phone ? (
                <a href={`tel:${String(merged.phone).replace(/\D/g, '')}`}>
                  {formatPhone(merged.phone)}
                </a>
              ) : (
                <Muted>Not provided</Muted>
              )}
            </dd>
            {merged.username && (
              <>
                <dt>Username</dt>
                <dd>
                  <span className="mym-mono">{merged.username}</span>
                </dd>
              </>
            )}
          </dl>
        </Section>

        <Section icon={faMapMarkedAlt} tone="warning" title="Work address">
          {addressLines.length > 0 ? (
            <address className="mb-0 fw-semibold text-900 fs-10">
              {addressLines.map(line => (
                <div key={line}>{line}</div>
              ))}
            </address>
          ) : (
            <Muted>Not provided</Muted>
          )}
        </Section>

        <Section icon={faFingerprint} tone="purple" title="Submission">
          <dl className="mym-kv">
            <dt>Submitted</dt>
            <dd>{formatDateTime(submittedAt) || <Muted>Unknown</Muted>}</dd>
            <dt>IP address</dt>
            <dd>
              {getIpAddress(merged) ? (
                <span className="mym-mono">{getIpAddress(merged)}</span>
              ) : (
                <Muted>
                  {detailReq.isLoading ? 'Loading…' : 'Not recorded'}
                </Muted>
              )}
            </dd>
            {userAgent && (
              <>
                <dt>Device</dt>
                <dd className="fw-normal fs-11 text-700">{userAgent}</dd>
              </>
            )}
            <dt>Reference</dt>
            <dd className="mym-mono">#{merged.id}</dd>
          </dl>
        </Section>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

RegistrationDetailDrawer.propTypes = {
  show: PropTypes.bool.isRequired,
  record: PropTypes.object,
  onHide: PropTypes.func.isRequired,
  getGroupName: PropTypes.func.isRequired,
  lookupFacility: PropTypes.func.isRequired
};

export default RegistrationDetailDrawer;
